import { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Table,
  Select,
  Button,
  Spin,
  App,
  Space,
  Form,
} from 'antd';
import {
  FileExcelOutlined,
  SearchOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import {
  listPlayerPositions,
  listPlayFrequencies,
  listFieldTypes,
} from '../../services/catalogs';
import { getKitCheckReport } from '../../services/reports';
import { exportToExcel } from '../../utils/excel';

const KitCheckReport = () => {
  const { message } = App.useApp();

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [positions, setPositions] = useState([]);
  const [playFrequency, setPlayFrequency] = useState([]);
  const [fieldTypes, setFieldTypes] = useState([]);

  const [form] = Form.useForm();

  useEffect(() => {
    Promise.all([
      listPlayerPositions(),
      listPlayFrequencies(),
      listFieldTypes(),
    ]).then(([p, pf, ft]) => {
      setPositions(p || []);
      setPlayFrequency(pf || []);
      setFieldTypes(ft || []);
    });
  }, []);

  const buildParams = () => {
    const values = form.getFieldsValue();
    const params = {};
    if (values.positionId != null) params.positionId = values.positionId;
    if (values.playFrequencyId != null)
      params.playFrequencyId = values.playFrequencyId;
    if (values.fieldTypeId != null) params.fieldTypeId = values.fieldTypeId;
    return params;
  };

  const hasAtLeastOneFilter = () => {
    const params = buildParams();
    return Object.keys(params).length >= 1;
  };

  const handleSearch = async () => {
    if (!hasAtLeastOneFilter()) {
      message.warning('Debe seleccionar al menos un filtro para consultar.');
      return;
    }
    setLoading(true);
    try {
      const params = buildParams();
      const res = await getKitCheckReport(params);
      if (res?.ok && res?.data) {
        setData(res.data.items || []);
        setTotalCount(res.data.total ?? 0);
      } else {
        setData([]);
        setTotalCount(0);
      }
    } catch (err) {
      message.error(
        err?.__normalized?.message || 'Error al obtener el reporte.',
      );
      setData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    form.resetFields();
    setData([]);
    setTotalCount(0);
  };

  const handleExportExcel = () => {
    if (!data || data.length === 0) {
      message.warning(
        'No hay datos para exportar. Ejecute primero la búsqueda.',
      );
      return;
    }
    setExporting(true);
    try {
      const excelData = data.map((item) => ({
        Posición: item.position ?? '',
        'Frecuencia de juego': item.playFrequency ?? '',
        'Tipo de cancha': item.fieldType ?? '',
        '#': item.count ?? 0,
      }));
      const columnWidths = [{ wch: 20 }, { wch: 18 }, { wch: 18 }, { wch: 12 }];
      exportToExcel(excelData, columnWidths, 'El Kit Check', 'kit_check');
      message.success('Archivo Excel exportado correctamente.');
    } catch (err) {
      message.error('Error al exportar a Excel.');
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      title: 'Posición',
      dataIndex: 'position',
      key: 'position',
      sorter: (a, b) => (a.position || '').localeCompare(b.position || ''),
      width: '30%',
    },
    {
      title: 'Frecuencia de juego',
      dataIndex: 'playFrequency',
      key: 'playFrequency',
      sorter: (a, b) =>
        (a.playFrequency || '').localeCompare(b.playFrequency || ''),
      width: '30%',
    },

    {
      title: 'Tipo de cancha',
      dataIndex: 'fieldType',
      key: 'fieldType',
      render: (v) => v ?? '-',
      sorter: (a, b) => (a.fieldType || '').localeCompare(b.fieldType || ''),
      width: '30%',
    },

    {
      title: '#',
      dataIndex: 'count',
      key: 'count',
      sorter: (a, b) => (a.count ?? 0) - (b.count ?? 0),
      defaultSortOrder: 'descend',
      width: '10%',
    },
  ];

  const dataSource = data.map((item, idx) => ({
    ...item,
    key: idx,
  }));

  return (
    <Card
      title="Reportes - El 'Kit Check'"
      style={{ margin: 20 }}
      extra={
        <Space>
          <Button
            icon={<SearchOutlined />}
            type="primary"
            onClick={handleSearch}
            loading={loading}
          >
            Buscar
          </Button>
          <Button icon={<ClearOutlined />} onClick={handleClear}>
            Limpiar
          </Button>
          <Button
            icon={<FileExcelOutlined />}
            onClick={handleExportExcel}
            loading={exporting}
            disabled={!data.length}
          >
            Exportar Excel
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" onFinish={handleSearch}>
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Form.Item name="positionId" label="Posición">
              <Select
                allowClear
                placeholder="Posición"
                options={positions.map((p) => ({
                  value: p.id,
                  label: p.name,
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Form.Item name="playFrequencyId" label="Frecuencia de juego">
              <Select
                allowClear
                placeholder="Frecuencia de juego"
                options={playFrequency.map((f) => ({
                  value: f.id,
                  label: f.name,
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Form.Item name="fieldTypeId" label="Tipo de cancha">
              <Select
                allowClear
                placeholder="Tipo cancha"
                options={fieldTypes.map((f) => ({
                  value: f.id,
                  label: f.name,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} registros`,
          }}
          scroll={{ x: 'max-content' }}
          summary={() =>
            totalCount > 0 ? (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={6}>
                  <strong>Total general</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <strong>{totalCount}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}>100%</Table.Summary.Cell>
              </Table.Summary.Row>
            ) : null
          }
        />
      </Spin>
    </Card>
  );
};

export default KitCheckReport;
